import { z } from 'zod';
import {
  type UseQueryResult,
  type QueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import { type ApiError } from '../../../../utils/api/ApiError/ApiError';
import { organizationSchema } from '../use-get-organization-by-id-api';

type PaginationData = {
  page?: number | string;
  size?: number | string;
};

const sortSchema = z.object({
  empty: z.boolean(),
  sorted: z.boolean(),
  unsorted: z.boolean(),
});

const organizationsByParentIdSchema = z.object({
  content: z.array(organizationSchema),
  empty: z.boolean(),
  first: z.boolean(),
  last: z.boolean(),
  number: z.number().int(),
  numberOfElements: z.number().int(),
  pageable: z.object({
    offset: z.number().int(),
    pageNumber: z.number().int(),
    pageSize: z.number().int(),
    paged: z.boolean(),
    sort: sortSchema,
    unpaged: z.boolean(),
  }),
  size: z.number().int(),
  sort: sortSchema,
  totalElements: z.number().int(),
  totalPages: z.number().int(),
});

export type OrganizationsByParentIdWithPagination = z.infer<
  typeof organizationsByParentIdSchema
>;

export const defaultChildOrgPaginationData: PaginationData = {
  page: 0,
  size: 20,
};

export async function getOrganizationByParentIdApi(
  orgId?: string,
  allChildren?: boolean,
  paginationData?: PaginationData
) {
  const paginationParams = {
    ...defaultChildOrgPaginationData,
    ...paginationData,
  };

  const orgs = await fetcher(`/organizations/parent-orgs/${orgId}`, {
    service: '/roster',
    version: '/v3',
    queryParameters: [
      ['allChildren', allChildren],
      ...Object.entries(paginationParams),
    ] as string[][],
  });
  return organizationsByParentIdSchema.parse(orgs);
}

export const getOrganizationByParentIdApiQueryKey = (
  args?: { orgId?: string; allChildren?: boolean } & Partial<PaginationData>
) => {
  const { orgId, allChildren, page, size } = args ?? {};
  return ['organization', 'parent-org', orgId, allChildren, page, size].filter(
    Boolean
  );
};

export function useGetOrganizationByParentIdApi(
  { orgId, allChildren = false }: { orgId?: string; allChildren?: boolean },
  paginationData: PaginationData = defaultChildOrgPaginationData,
  customQueryOptions?: QueryOptions<
    OrganizationsByParentIdWithPagination,
    ApiError
  >
): UseQueryResult<OrganizationsByParentIdWithPagination, ApiError> {
  return useQuery({
    queryKey: getOrganizationByParentIdApiQueryKey({
      orgId,
      allChildren,
      ...paginationData,
    }),
    queryFn: () =>
      getOrganizationByParentIdApi(orgId, allChildren, paginationData),
    enabled: Boolean(orgId),
    ...customQueryOptions,
  });
}
